import { CartUpdateEvent } from '@theme/events';

/**
 * Cart-line quantity control for FREE SAMPLES (snippets/dgt-sample-qty.liquid).
 *
 * Lets shoppers add more than one of a sample, but never past the allowance
 * (1 per order + 1 per $20 of paid total, capped at 5). The plus button is
 * disabled in Liquid when the cart is already at the limit; this handler ALSO
 * re-checks the allowance against the authoritative /cart.js right before adding,
 * so two fast clicks on different sample lines can't race past the cap.
 *
 * Like dgt-pack-qty, we change the line WITHOUT requesting sections so Horizon's
 * cart-items-component re-renders the section itself and rebuilds our label +
 * the disabled state from Liquid.
 */

const CART_ROOT = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
const CART_URL = () => `${CART_ROOT()}cart.js`;
const CART_CHANGE_URL = () => `${CART_ROOT()}cart/change.js`;
const SAMPLE_PROPERTY = '_sample';

const inFlight = new Set();

function isSample(item) {
  return item.product_type === 'Sample Product' || (item.properties && item.properties[SAMPLE_PROPERTY]);
}

function sampleAllowance(cart) {
  const sampleUnits = (cart.items || []).filter(isSample).reduce((n, i) => n + i.quantity, 0);
  const paidUnits = (cart.items || []).filter((i) => !isSample(i)).reduce((n, i) => n + i.quantity, 0);
  const allowance = paidUnits === 0 ? 0 : Math.min(5, Math.floor(cart.total_price / 2000) + 1);
  return { sampleUnits, allowance };
}

async function step(cell, isPlus) {
  const lineKey = cell.dataset.lineKey;
  if (!lineKey || inFlight.has(lineKey)) return;

  inFlight.add(lineKey);
  cell.classList.add('is-busy');
  try {
    const cart = await (await fetch(CART_URL(), { cache: 'no-store' })).json();
    const line = (cart.items || []).find((i) => i.key === lineKey);
    const current = line ? line.quantity : 0;

    if (isPlus) {
      // Authoritative allowance check — block the add if the cart is already at (or over) the cap.
      const { sampleUnits, allowance } = sampleAllowance(cart);
      if (sampleUnits >= allowance) return;
    }

    let next = isPlus ? current + 1 : current - 1;
    if (next < 1) next = 0; // below one -> remove the line

    const component = cell.closest('cart-items-component');
    const sectionId = component ? component.dataset.sectionId : '';

    const response = await fetch(CART_CHANGE_URL(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: next }),
    });
    const data = await response.json();

    cell.dispatchEvent(new CartUpdateEvent(data, sectionId || '', { source: 'dgt-sample-qty' }));
  } catch (error) {
    console.error('[dgt-sample-qty] step failed', error);
  } finally {
    inFlight.delete(lineKey);
    cell.classList.remove('is-busy');
  }
}

document.addEventListener('click', (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-sq-plus],[data-sq-minus]') : null;
  if (!btn || btn.hasAttribute('disabled')) return;
  const cell = btn.closest('[data-dgt-sample-qty]');
  if (!cell) return;
  event.preventDefault();
  step(cell, btn.hasAttribute('data-sq-plus'));
});
